import { Waves, Trees, CookingPot, Wifi, Wind, SquareParking, Flame, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const ICONS = [Waves, Trees, CookingPot, Wifi, Wind, SquareParking, Flame, Sparkles];

const IMG_POOL = "https://a0.muscache.com/im/pictures/9f3db89c-15e5-4694-be00-7ccd6f19522e.jpg?im_w=1600";
const IMG_NATURE = "https://a0.muscache.com/im/pictures/158222e0-9080-4c26-913b-eeba780875d0.jpg?im_w=1200";
const IMG_INSIDE = "https://a0.muscache.com/im/pictures/de6a7fe5-8e40-4dc4-b199-d1f2c752b2eb.jpg?im_w=1200";

const ITEMS = {
  it: [
    "Piscina a sfioro ad acqua salata (apr–nov)",
    "Vasca idromassaggio privata",
    "Giardino tropicale di 1000 mq",
    "Wi-Fi veloce 67 Mbps",
    "Aria condizionata in tutte le camere",
    "Barbecue e cena tipica su richiesta",
    "Lavatrice e lavastoviglie",
    "Parcheggio gratuito in loco",
  ],
  en: [
    "Infinity salt-water pool (Apr–Nov)",
    "Private hot tub",
    "1000 sqm tropical fenced garden",
    "Fast Wi-Fi 67 Mbps",
    "Air conditioning in every room",
    "Barbecue and typical dinner on request",
    "Washer and dishwasher",
    "Free on-site parking",
  ],
};

function ImgCard({ src, alt, chip, className }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-lg border border-line ${className}`}
      data-testid="amenity-image-card"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
      {chip && (
        <span className="absolute bottom-4 left-4 rounded-full bg-sand/85 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink backdrop-blur-md">
          {chip}
        </span>
      )}
    </div>
  );
}
export default function Amenities() {
  const { t, lang } = useLang();
  const chips =
    lang === "it"
      ? { pool: "Piscina privata", nature: "Giardino tropicale", inside: "Le camere" }
      : { pool: "Private pool", nature: "Tropical garden", inside: "The bedrooms" };
  const items = ITEMS[lang] || ITEMS.it;

  return (
    <section id="comfort" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
      <div className="mb-14 grid gap-8 md:mb-20 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <Reveal>
            <Eyebrow>{t.comfort.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-serif text-4xl font-light leading-[1.05] md:text-6xl">
              {t.comfort.title}
            </h2>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:auto-rows-[220px]">
        <Reveal className="md:col-span-7 md:row-span-2" delay={0}>
          <ImgCard src={IMG_POOL} alt="La piscina e la villa" chip={chips.pool} className="h-[340px] w-full md:h-full" />
        </Reveal>

        <Reveal className="md:col-span-5 md:row-span-2" delay={0.1}>
          <div className="flex h-full flex-col justify-between rounded-lg bg-ink p-7 text-sand md:p-9" data-testid="amenities-list-card">
            <h3 className="font-serif text-2xl font-light italic text-sand/90 md:text-3xl">
              {t.comfort.listTitle}
            </h3>
            <ul className="mt-6 grid gap-3 md:gap-2.5">
              {items.map((item, i) => {
                const Icon = ICONS[i];
                return (
                  <li
                    key={item}
                    className="flex items-center gap-3 border-b border-sand/10 pb-2.5 text-sm text-sand/85 last:border-0 md:text-[15px]"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-terra" />
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        </Reveal>

        <Reveal className="md:col-span-4" delay={0}>
          <ImgCard src={IMG_NATURE} alt="Ulivi al tramonto" chip={t.comfort.chipNature} className="h-[260px] w-full md:h-full" />
        </Reveal>

        <Reveal className="md:col-span-4" delay={0.1}>
          <div className="flex h-full min-h-[260px] flex-col justify-between rounded-lg bg-terra p-7 text-sand md:h-full md:p-8" data-testid="amenities-quote-card">
            <p className="font-serif text-3xl font-light italic leading-tight md:text-[34px]">
              “{t.comfort.quote}”
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-sand/80">
              {t.comfort.quoteSub}
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-4" delay={0.2}>
          <ImgCard src={IMG_INSIDE} alt="Interno curato della villa" chip={t.comfort.chipInside} className="h-[260px] w-full md:h-full" />
        </Reveal>
      </div>
    </section>
  );
}
